import { equals, mergeDeepRight } from 'ramda';
import { Component, createRef, ReactNode } from 'react';
import { Box, CarouselControls, FelaCSS, TSpacingValue } from '@bridebook/ui';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import { IconChevronFat } from '@bridebook/ui/src/icons/dynamic';
import Swipeable from '../../swipeable/swipeable';
import Slide from '../slide/slide';
import componentStyles from './simple-slider-desktop.style';

export type CarouselNavigationType = 'overlay' | 'top';

interface IDefaultProps {
  slidesToShow: number;
  slideSpeed: number;
  showNav: boolean;
  lazyLoad: boolean;
  slides: { key: string }[];
  navigationType?: CarouselNavigationType;
  headerButtons?: ReactNode;
  controlsStyles: FelaCSS;
  containerStyles?: FelaCSS;
}

interface IProps extends IDefaultProps {}

interface IProps {
  aspectRatio?: number;
  onSlide?: (index: number) => void;
  noReinit?: boolean;
  loader?: ReactNode;
  noSSR: boolean;
  fixedWidthSlide?: number;
  headerTitle?: ReactNode;
  headingStyles?: FelaCSS;
  slideStyles?: FelaCSS;
  gap?: TSpacingValue;
}

interface IState {
  currentIndex: number;
  listWidth: number;
  slideWidth: number;
  initialized: boolean;
}

const STATIC_SLIDE_WIDTH = 300;

class SimpleSliderDesktop extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const { noSSR, slides, fixedWidthSlide } = props;
    const slideWidth = noSSR ? 0 : STATIC_SLIDE_WIDTH;
    this.state = {
      currentIndex: 0,
      slideWidth: fixedWidthSlide ? fixedWidthSlide : slideWidth,
      listWidth: noSSR ? 4000 : slides.length * (fixedWidthSlide || STATIC_SLIDE_WIDTH),
      // Initialize immediately in logged out mode
      initialized: !noSSR,
    };
  }

  node = createRef();

  static defaultProps: IDefaultProps = {
    slidesToShow: 1,
    controlsStyles: {},
    slideSpeed: 500,
    navigationType: 'overlay',
    showNav: false,
    lazyLoad: false,
    slides: [],
  };

  componentDidMount() {
    if (this.props.slides.length) {
      this.initialize();
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (!equals(prevProps.slides, this.props.slides) && !this.props.noReinit) {
      this.initialize();
    }
  }

  initialize = () => {
    if (this.node && this.node.current) {
      const calculatedSlideWidth =
        this.getWidth(this.node.current) / this.props.slidesToShow + 16 / this.props.slidesToShow;
      const slideWidth = this.props.fixedWidthSlide
        ? this.props.fixedWidthSlide
        : calculatedSlideWidth;

      const listWidth = slideWidth * this.props.slides.length;
      this.setState({
        slideWidth,
        listWidth,
        currentIndex: 0,
        initialized: true,
      });
    }
  };

  getWidth = (elem: any): number => elem.getBoundingClientRect().width || elem.offsetWidth;

  previousSlide = () => {
    if (this.canSlideLeft()) {
      const newIndex = this.state.currentIndex - 1;
      this.setState({ currentIndex: newIndex });
      if (this.props.onSlide) {
        this.props.onSlide(newIndex);
      }
    }
  };

  nextSlide = () => {
    if (this.canSlideRight()) {
      const newIndex = this.state.currentIndex + 1;
      this.setState({ currentIndex: newIndex });
      if (this.props.onSlide) {
        this.props.onSlide(newIndex);
      }
    }
  };

  canSlideRight = () => {
    if (this.props.fixedWidthSlide) {
      const rightSideScrollableWidth = this.getRightSideScrollableWidth();
      return rightSideScrollableWidth >= 0;
    }
    return this.state.currentIndex + this.props.slidesToShow < this.props.slides.length;
  };

  getRightSideScrollableWidth = () => {
    const shiftLeft = this.state.currentIndex * (this.props.fixedWidthSlide || 0);
    const sliderWindowWidth = this.node?.current ? this.getWidth(this.node.current) : 0;
    return this.state.listWidth - shiftLeft - sliderWindowWidth;
  };

  canSlideLeft = () => this.state.currentIndex > 0;

  render() {
    const {
      slides,
      slideSpeed,
      showNav,
      navigationType = 'overlay',
      aspectRatio,
      lazyLoad,
      loader,
      noSSR,
      headerTitle,
      headerButtons = null,
      controlsStyles,
      slideStyles,
      headingStyles,
      containerStyles = {},
      gap,
    } = this.props;

    const { listWidth, slideWidth, currentIndex, initialized } = this.state;

    let slidePosition = currentIndex * slideWidth * -1;
    if (this.props.fixedWidthSlide && this.node?.current) {
      const sliderWindowWidth = this.getWidth(this.node.current);
      const mostRightPosition = Math.max(listWidth - sliderWindowWidth, 0);
      slidePosition = -1 * Math.min(currentIndex * slideWidth, mostRightPosition);
    }
    const slideHeight = aspectRatio ? slideWidth / aspectRatio : 'auto';

    const styles = componentStyles({
      slideSpeed,
      slidePosition,
      slideWidth,
      headerTitle: !!headerTitle,
      gap,
    });

    const showControls = navigationType === 'top' && showNav && noSSR;
    const showTopNavigation = showControls || !!headerButtons;
    return (
      <Box w="100%" style={styles.wrapper} setRef={this.node} data-name="simple-slider-desktop">
        {(headerTitle || showTopNavigation) && (
          <Box style={{ ...styles.headerControlsWrapper, ...(this.props.headingStyles || {}) }}>
            {headerTitle &&
              (typeof headerTitle === 'string' ? (
                <Box style={styles.carouselTitle}>
                  <Box as="h4" style={headingStyles ? headingStyles : styles.heading}>
                    {headerTitle}
                  </Box>
                </Box>
              ) : (
                headerTitle
              ))}
            {showTopNavigation && (
              <Box
                style={mergeDeepRight(styles.controls, controlsStyles) as FelaCSS}
                data-name="slider-controls">
                {headerButtons}
                {showControls && (
                  <CarouselControls
                    onPrev={this.previousSlide}
                    prevDisabled={!this.canSlideLeft()}
                    onNext={this.nextSlide}
                    nextDisabled={!this.canSlideRight()}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
        <Swipeable onSwipedLeft={this.nextSlide} onSwipedRight={this.previousSlide}>
          <Box style={styles.viewContainer}>
            <Box
              w={this.state.listWidth}
              h={slideHeight}
              style={mergeStyles([styles.slidesContainer, containerStyles])}>
              {initialized && (
                <>
                  {slides.map((item: any, index) => (
                    <Box
                      key={`simple-slider-desktop-box-${index}`}
                      style={mergeStyles([styles.slideWrapper, slideStyles || {}])}>
                      <Slide
                        key={item.key}
                        loader={loader}
                        active={index === currentIndex || index === currentIndex + 1}
                        lazyLoad={lazyLoad}>
                        {item}
                      </Slide>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Swipeable>
        {navigationType === 'overlay' && showNav && noSSR && (
          <>
            {this.canSlideLeft() && (
              <Box
                as="button"
                w={44}
                h={44}
                p="0"
                style={styles.buttonPrevious}
                onClick={this.previousSlide}>
                <Box style={styles.iconWrapBack}>
                  <IconChevronFat color="white" width={18} />
                </Box>
              </Box>
            )}
            {this.canSlideRight() && (
              <Box
                as="button"
                w={44}
                h={44}
                p="0"
                style={styles.buttonNext}
                onClick={this.nextSlide}>
                <Box style={styles.iconWrapNext}>
                  <IconChevronFat color="white" width={18} />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }
}

export default SimpleSliderDesktop;
